import { FC, Fragment, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import MainLayout from 'views/layouts/Main'
import { H2, H4, P } from 'views/components/UI/Text'
import Input from 'views/components/UI/TextInput'
import { Button } from 'views/components/UI/Buttons'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import ModalLoader from 'views/components/UI/ModalLoader/modal-loader'
import ModalAddSkill from 'views/containers/RoleFamily/skills/edit/modal-add-skills'
import SaveModalConfirmation from '../IdealProfileBetesa/components/SaveModalConfirmation'
import useReportDownloaderByExcel from 'hooks/process/useGetExcelReportByProcess/use-get-excel-report-by-process'
import { useNotification } from 'lib/context/notification.context'
import { generalDataSelector } from 'ducks/auth'
import { CompetenceSkills } from 'types/common'
import { fetchUpdateSkillsIdealProfile } from 'views/screens/Platform-administrator/services/fetchUpdateSkillsIdealProfile'
import {
  Container,
  HeadContainer,
  Row,
  GridContainer,
  TitleGrid,
  Divider,
  GridCompetences,
  GridItem,
  GridMinimunRecord,
  FooterContent,
  BackButton,
  TextBackButton,
} from 'views/containers/RoleFamily/betesa-skills/edit/styled'
import ArrowLeftIcon from 'assets/icons/ArrowLeftIcon'
import { StrokedButton } from 'views/components/UI/Buttons'

type EditableSkill = CompetenceSkills & {
  skill?: string
  weight: number
}

const EditIdealProfileSkills: FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { fetchGetReportsByExcel } = useReportDownloaderByExcel()
  const { getSuccess, getWarning } = useNotification()
  const { data: generalData } = useSelector(generalDataSelector)

  const idealProfileSkillsState = location.state?.idealProfileSkills || []
  const skillsTestId = location.state?.skillsTestId
  const processId = location.state?.processId
  const processName = location.state?.processName
  const levelName = location.state?.levelName

  const [showModal, setShowModal] = useState(false)
  const [showAddSkillModal, setShowAddSkillModal] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [skills, setSkills] = useState<EditableSkill[]>(
    idealProfileSkillsState.map((skill: any) => ({
      id: skill.skillId || skill.skill_id,
      skill_id: skill.skillId || skill.skill_id,
      skill: skill.skillName,
      skill_name: skill.skillName,
      weight: Number(skill.skillWeight || 0),
      test_category_id: skill.testCategoryId || 0,
    })),
  )

  const selectedSkillIds = useMemo(
    () => skills.map((skill) => skill.skill_id || skill.id),
    [skills],
  )

  const levelId = useMemo(() => {
    return generalData?.level?.find((level: any) => level.name === levelName)?.id
  }, [generalData, levelName])

  const hasInvalidWeights =
    skills.length === 0 ||
    skills.some(
      (skill) =>
        skill.weight === undefined ||
        Number(skill.weight) <= 0 ||
        Number(skill.weight) > 100,
    )

  const handleSaveClick = () => {
    if (skills.length === 0) {
      getWarning('Debe agregar al menos una competencia')
      return
    }

    if (hasInvalidWeights) {
      getWarning('Debe agregar un puntaje mínimo válido a cada competencia')
      return
    }

    setShowModal(true)
  }

  const handleConfirmSave = async () => {
    if (!skillsTestId || !processId) {
      getWarning('No fue posible identificar el proceso a actualizar')
      return
    }

    setIsLoading(true)

    try {
      await fetchGetReportsByExcel({
        processId,
        useBetesa: false,
        useCompetence: true,
        processName,
      })

      await fetchUpdateSkillsIdealProfile(
        skillsTestId,
        skills.map((skill) => ({
          skill_id: Number(skill.skill_id || skill.id),
          weight: Number(skill.weight),
        })),
      )

      getSuccess('Perfil ideal para 3D Skills actualizado correctamente')
      navigate(-1)
    } catch (error) {
      getWarning('Ocurrió un error al actualizar el perfil ideal para 3D Skills')
    } finally {
      setIsLoading(false)
      setShowModal(false)
    }
  }

  return (
    <MainLayout>
      <Container>
        <H2>Edita el perfil ideal para 3D Skills</H2>
        <HeadContainer>
          <Row width="50%">
            <H4 margin="0">Competencias 3D Skills asociadas</H4>
          </Row>
          <Row justify="flex-end" width="50%">
            <StrokedButton
              onClick={() => {
                if (!levelId) {
                  getWarning('No fue posible determinar el nivel del proceso')
                  return
                }

                setShowAddSkillModal(true)
              }}
            >
              Agregar 3D Skills
            </StrokedButton>
          </Row>
        </HeadContainer>

        <GridContainer>
          <TitleGrid>Nombre</TitleGrid>
          <Divider />
          <GridCompetences>
            {skills.map((skill) => (
              <Fragment key={`skill-row-${skill.id}`}>
                <GridItem justify="center">
                  <P>{skill.skill || skill.skill_name}</P>
                </GridItem>
                <GridItem>
                  <Row height="40px" justify="flex-end">
                    <DeleteOutlineOutlinedIcon
                      onClick={() =>
                        setSkills((prev) =>
                          prev.filter(
                            (item) =>
                              Number(item.skill_id || item.id) !==
                              Number(skill.skill_id || skill.id),
                          ),
                        )
                      }
                      style={{
                        color: '#FF0000',
                        cursor: 'pointer',
                        marginRight: '20px',
                      }}
                    />
                  </Row>
                </GridItem>
              </Fragment>
            ))}
          </GridCompetences>
        </GridContainer>

        <GridContainer>
          <H4 marginTop="30px">Puntaje mínimo por competencia</H4>
          <TitleGrid>Nombre</TitleGrid>
          <Divider />
          <GridMinimunRecord>
            {skills.map((skill) => (
              <Fragment key={`skill-weight-${skill.id}`}>
                <GridItem justify="center">
                  <P>{skill.skill || skill.skill_name}</P>
                </GridItem>
                <GridItem>
                  <Row width="50%" justify="flex-start">
                    <Input
                      name={`minimum-score-${skill.id}`}
                      value={skill.weight || ''}
                      label="Puntaje mínimo"
                      type="number"
                      onChange={(e: any) => {
                        let value = Number(e.target.value)
                        if (Number.isNaN(value) || value < 0) value = 0
                        if (value > 100) value = 100

                        setSkills((prev) =>
                          prev.map((item) =>
                            Number(item.skill_id || item.id) ===
                            Number(skill.skill_id || skill.id)
                              ? { ...item, weight: value }
                              : item,
                          ),
                        )
                      }}
                    />
                  </Row>
                </GridItem>
              </Fragment>
            ))}
          </GridMinimunRecord>
        </GridContainer>

        <FooterContent>
          <BackButton onClick={() => navigate(-1)}>
            <ArrowLeftIcon />
            <TextBackButton>Atrás</TextBackButton>
          </BackButton>
          <Button
            disabled={hasInvalidWeights}
            onClick={handleSaveClick}
            onDisabledClick={() => {
              if (skills.length === 0) {
                getWarning('Debe agregar al menos una competencia')
                return
              }

              getWarning('Debe agregar un puntaje mínimo válido a cada competencia')
            }}
            type="submit"
          >
            Guardar
          </Button>
        </FooterContent>

        <ModalAddSkill
          open={showAddSkillModal}
          setOpen={setShowAddSkillModal}
          skillsSelected={selectedSkillIds}
          levelId={levelId}
          onAddNewSkill={(newSkill: any) => {
            if (selectedSkillIds.includes(newSkill.id)) {
              getWarning('Ya existe esta competencia')
              return
            }

            setSkills((prev) => [
              ...prev,
              {
                id: newSkill.id,
                skill_id: newSkill.id,
                skill: newSkill.skill,
                skill_name: newSkill.skill,
                weight: 0,
                test_category_id: 0,
              },
            ])
          }}
        />

        <SaveModalConfirmation
          show={showModal}
          setShow={setShowModal}
          onSave={handleConfirmSave}
          isLoading={isLoading}
        />
        <ModalLoader isLoading={isLoading} />
      </Container>
    </MainLayout>
  )
}

export default EditIdealProfileSkills
